import { onMounted, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { storeToRefs } from 'pinia';

import { ensureHasAccess } from '../access/ensure-has-access';
import { useAccessControlStore } from '../store/access-control.store';

interface UseOnboardedEffectOptions {
  dedupeKey?: MaybeRefOrGetter<string | null | undefined>;
}

export const useOnboardedEffect = (
  effect: () => void | Promise<unknown>,
  options: UseOnboardedEffectOptions = {},
) => {
  const accessControlStore = useAccessControlStore();
  const { isOnboarded } = storeToRefs(accessControlStore);
  const { ensureAccessResolved } = accessControlStore;
  const { dedupeKey } = options;

  let inFlightPromise: Promise<unknown> | null = null;
  let lastCompletedKey: string | null = null;

  const readDedupeKey = () => {
    return dedupeKey ? (toValue(dedupeKey) ?? null) : null;
  };

  const runEffect = () => {
    const key = readDedupeKey();

    if (key && lastCompletedKey === key) {
      return inFlightPromise ?? Promise.resolve();
    }

    if (inFlightPromise) {
      return inFlightPromise;
    }

    inFlightPromise = Promise.resolve(effect())
      .then(() => {
        if (key) {
          lastCompletedKey = key;
        }
      })
      .finally(() => {
        inFlightPromise = null;
      });

    return inFlightPromise;
  };

  watch(
    readDedupeKey,
    (key, previousKey) => {
      if (key === previousKey) return;

      lastCompletedKey = null;
    },
    { immediate: true },
  );

  onMounted(() => {
    if (ensureHasAccess({ showToast: false })) {
      return;
    }

    void ensureAccessResolved();
  });

  watch(
    isOnboarded,
    (onboarded) => {
      if (!onboarded) return;

      void runEffect();
    },
    { immediate: true },
  );
};
